import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { BonLivraison, BonLivraisonStatus } from '../../types/bonLivraison';
import { User } from '../../types/auth';
import { Client } from '../../types/client';
import { bonsLivraisonAPI, userAPI, clientsAPI, auditLogsAPI } from '../../services/api';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { FilterListOff as FilterListOffIcon } from '@mui/icons-material';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import usePdfPreview from '../../hooks/usePdfPreview';
import BonLivraisonFormDialog from './BonLivraisonFormDialog';
import BonLivraisonPreview from './BonLivraisonPreview';
import type { BonLivraisonPDFOptions } from '../../utils/bonLivraisonPdfReactPdf';
// generateBonLivraisonPDF is dynamically imported in the print handler so the
// @react-pdf/renderer chunk only loads when the user clicks Print.

const COMMERCIAL_COLOR = '#4CAF50';

const BL_FILTER_SCHEMA = {
  date:        { type: 'dateRange',    paramFrom: 'date_from', paramTo: 'date_to' },
  reference:   { type: 'text',         param: 'reference' },
  bc_numero:   { type: 'text',         param: 'bc_numero' },
  client_id:   { type: 'autocomplete', param: 'client_id', transform: (v: string) => Number(v) },
  designation: { type: 'text',         param: 'designation' },
  status:      { type: 'select',       param: 'status' },
  user_id:     { type: 'autocomplete', param: 'user_id', transform: (v: string) => Number(v) },
} as const;

const BonLivraisonList: React.FC = () => {
  const [blList, setBlList] = useState<BonLivraison[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedBlList, setDeletedBlList] = useState<BonLivraison[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [previewBl, setPreviewBl] = useState<BonLivraison | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editBlId, setEditBlId] = useState<number | undefined>(undefined);
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [printBl, setPrintBl] = useState<BonLivraison | null>(null);
  const [printOptions, setPrintOptions] = useState<BonLivraisonPDFOptions>({
    includeTransportInfo: true,
    includeCreatedBy: true,
  });
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  // Column filters
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    BL_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  // Fetch users + clients for filter options
  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          userAPI.getUsers(0, 1000),
          clientsAPI.getAllForDropdown(),
        ]);
        setUsers(u);
        setClients(c);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    })();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchBonsLivraison = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const { status, client_id, user_id, ...extra } = toQueryParams();
      const response = await bonsLivraisonAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        (status as string) || undefined,
        (client_id as number | undefined),
        (user_id as number | undefined),
        Object.keys(extra).length > 0 ? extra : undefined
      );
      setBlList(response.items);
      setTotalCount(response.total);
    } catch (err) {
      showError('Erreur lors du chargement des bons de livraison');
      console.error('Error fetching bons de livraison:', err);
      setBlList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const fetchDeletedBonsLivraison = async () => {
    try {
      setLoading(true);
      const data = await bonsLivraisonAPI.getDeleted();
      setDeletedBlList(data);
    } catch (err) {
      showError('Erreur lors du chargement des bons de livraison supprimés');
      console.error('Error fetching deleted bons de livraison:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedBonsLivraison();
    }
  };

  const handleRestore = async (bl: BonLivraison) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer le bon de livraison "${bl.reference}" ?`,
      async () => {
        try {
          await bonsLivraisonAPI.restore(bl.id);
          showSuccess('Bon de livraison restauré avec succès');
          fetchDeletedBonsLivraison();
          fetchBonsLivraison();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler',
      }
    );
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchBonsLivraison();
    }
  }, [fetchBonsLivraison, tabValue]);

  const handleDelete = async (bl: BonLivraison) => {
    try {
      await bonsLivraisonAPI.delete(bl.id);
      showSuccess('Bon de livraison supprimé avec succès');
      fetchBonsLivraison();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleOpenPrintOptions = (bl: BonLivraison) => {
    setPrintBl(bl);
    setPrintOptionsOpen(true);
  };

  const handleClosePrintOptions = () => {
    setPrintOptionsOpen(false);
    setPrintBl(null);
  };

  const handlePrint = () => {
    if (printBl) {
      const bl = printBl;
      const options = printOptions;
      openPreview({
        getBlob: async () => {
          const { getBonLivraisonPDFBlob } = await import('../../utils/bonLivraisonPdfReactPdf');
          return getBonLivraisonPDFBlob(bl, options);
        },
        fileName: `BL_${bl.reference}.pdf`,
      });
      auditLogsAPI.logPdfDownload('BonLivraison', bl.id, `Aperçu PDF - ${bl.reference}`);
      handleClosePrintOptions();
    }
  };

  const handleOpenNewBl = () => {
    setEditBlId(undefined);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleOpenEditBl = (blId: number) => {
    setEditBlId(blId);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setFormMinimized(false);
    setEditBlId(undefined);
  };

  const handleFormSuccess = () => {
    fetchBonsLivraison();
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case BonLivraisonStatus.EN_COURS:
        return <Chip label="En cours" color="warning" size="small" />;
      case BonLivraisonStatus.LIVRE:
        return <Chip label="Livré" color="success" size="small" />;
      case BonLivraisonStatus.ANNULE:
        return <Chip label="Annulé" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get all designations as comma-separated string (for display)
  const getDesignations = (bl: BonLivraison): string => {
    if (!bl.items || bl.items.length === 0) {
      return '-';
    }
    return bl.items.map(item => item.designation).join(', ');
  };

  // Get designations for tooltip (one per line)
  const getDesignationsForTooltip = (bl: BonLivraison): React.ReactNode => {
    if (!bl.items || bl.items.length === 0) {
      return '-';
    }
    return (
      <Box>
        {bl.items.map((item, index) => (
          <Box key={index}>• {item.designation}</Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', py: 3, px: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestion des Bons de Livraison</Typography>
        {tabValue === 0 && (
          <PermissionButton
            moduleName="Commercial"
            operation="add"
            miniModuleName="Bons Livraison"
            variant="contained"
            onClick={handleOpenNewBl}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#388E3C',
              },
            }}
          >
            Nouveau BL
          </PermissionButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Bons de Livraison Actifs (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder="Rechercher par référence, client ou BC..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 350 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                Effacer les filtres
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date
                      <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Réf. BL
                      <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      BC N°
                      <ColumnFilter type="text" value={filters.bc_numero} onChange={(v) => update('bc_numero', v)} label="BC N°" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Client
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.client_id}
                        onChange={(v) => update('client_id', v)}
                        label="Client"
                        color={COMMERCIAL_COLOR}
                        options={clients.map(c => ({ value: c.id.toString(), label: c.name }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Désignation
                      <ColumnFilter type="text" value={filters.designation} onChange={(v) => update('designation', v)} label="désignation" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Statut
                      <ColumnFilter
                        type="select"
                        value={filters.status}
                        onChange={(v) => update('status', v)}
                        label="statut"
                        color={COMMERCIAL_COLOR}
                        options={[
                          { value: BonLivraisonStatus.EN_COURS, label: 'En cours' },
                          { value: BonLivraisonStatus.LIVRE, label: 'Livré' },
                          { value: BonLivraisonStatus.ANNULE, label: 'Annulé' },
                        ]}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Créé par
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.user_id}
                        onChange={(v) => update('user_id', v)}
                        label="créé par"
                        color={COMMERCIAL_COLOR}
                        options={users.map(u => ({ value: u.id.toString(), label: u.full_name || u.email }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : blList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Aucun bon de livraison trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  blList.map((bl) => {
                    const itemCount = bl.items?.length || 1;
                    return bl.items && bl.items.length > 0 ? (
                      bl.items.map((item, index) => (
                        <TableRow key={`${bl.id}-${index}`}>
                          {index === 0 && (
                            <>
                              <TableCell rowSpan={itemCount} sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                {new Date(bl.date).toLocaleDateString('fr-FR')}
                              </TableCell>
                              <TableCell rowSpan={itemCount} sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                {bl.reference}
                              </TableCell>
                              <TableCell rowSpan={itemCount} sx={{ verticalAlign: 'middle' }}>
                                <Tooltip title={bl.bon_commande_number || '-'} arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 120,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {bl.bon_commande_number || '-'}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell rowSpan={itemCount} sx={{ verticalAlign: 'middle' }}>
                                <Tooltip title={bl.client_name} arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 150,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {bl.client_name}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ borderLeft: '1px solid rgba(224, 224, 224, 1)' }}>
                            <Tooltip title={item.designation} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 250,
                                  cursor: 'pointer',
                                }}
                              >
                                {item.designation}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          {index === 0 && (
                            <>
                              <TableCell rowSpan={itemCount} align="center" sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap', borderLeft: '1px solid rgba(224, 224, 224, 1)' }}>
                                {getStatusChip(bl.status)}
                              </TableCell>
                              <TableCell rowSpan={itemCount} sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                {bl.user_name || '-'}
                              </TableCell>
                              <TableCell rowSpan={itemCount} align="center" sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <Tooltip title="Imprimer" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenPrintOptions(bl)}
                                  >
                                    <PrintIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Aperçu" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => setPreviewBl(bl)}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                </Tooltip>
                                <PermissionButton
                                  moduleName="Commercial"
                                  operation="edit"
                                  miniModuleName="Bons Livraison"
                                  component="IconButton"
                                  onClick={() => handleOpenEditBl(bl.id)}
                                >
                                  <EditIcon />
                                </PermissionButton>
                                <PermissionButton
                                  moduleName="Commercial"
                                  operation="delete"
                                  miniModuleName="Bons Livraison"
                                  component="IconButton"
                                  onClick={() => {
                                    showConfirm(
                                      `Êtes-vous sûr de vouloir supprimer le bon de livraison "${bl.reference}" ?`,
                                      () => handleDelete(bl),
                                      {
                                        title: 'Confirmer la suppression',
                                        confirmText: 'Supprimer',
                                        cancelText: 'Annuler',
                                      }
                                    );
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </PermissionButton>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow key={bl.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(bl.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{bl.reference}</TableCell>
                        <TableCell>
                          <Tooltip title={bl.bon_commande_number || '-'} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 120,
                                cursor: 'pointer',
                              }}
                            >
                              {bl.bon_commande_number || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={bl.client_name} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 150,
                                cursor: 'pointer',
                              }}
                            >
                              {bl.client_name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={getDesignationsForTooltip(bl)} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 250,
                                cursor: 'pointer',
                              }}
                            >
                              {getDesignations(bl)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">{getStatusChip(bl.status)}</TableCell>
                        <TableCell>{bl.user_name || '-'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Imprimer" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPrintOptions(bl)}
                            >
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Aperçu" arrow>
                            <IconButton
                              size="small"
                              onClick={() => setPreviewBl(bl)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="edit"
                            miniModuleName="Bons Livraison"
                            component="IconButton"
                            onClick={() => handleOpenEditBl(bl.id)}
                          >
                            <EditIcon />
                          </PermissionButton>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="delete"
                            miniModuleName="Bons Livraison"
                            component="IconButton"
                            onClick={() => {
                              showConfirm(
                                `Êtes-vous sûr de vouloir supprimer le bon de livraison "${bl.reference}" ?`,
                                () => handleDelete(bl),
                                {
                                  title: 'Confirmer la suppression',
                                  confirmText: 'Supprimer',
                                  cancelText: 'Annuler',
                                }
                              );
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </PermissionButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Réf. BL</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="center">Statut</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedBlList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucun bon de livraison supprimé
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedBlList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((bl) => (
                      <TableRow key={bl.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(bl.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{bl.reference}</TableCell>
                        <TableCell>
                          <Tooltip title={bl.client_name} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 150,
                                cursor: 'pointer',
                              }}
                            >
                              {bl.client_name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">{getStatusChip(bl.status)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {bl.deleted_at
                            ? new Date(bl.deleted_at).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Restaurer" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleRestore(bl)}
                              color="primary"
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedBlList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Preview Modal */}
      {previewBl && (
        <BonLivraisonPreview
          bonLivraison={previewBl}
          open={!!previewBl}
          onClose={() => setPreviewBl(null)}
          onPrint={() => {
            setPreviewBl(null);
            handleOpenPrintOptions(previewBl);
          }}
          onEdit={() => {
            setPreviewBl(null);
            handleOpenEditBl(previewBl.id);
          }}
        />
      )}

      {/* Print Options Dialog */}
      <Dialog open={printOptionsOpen} onClose={handleClosePrintOptions} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#4CAF50', fontWeight: 600 }}>
          Options d'impression
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.includeTransportInfo}
                  onChange={(e) =>
                    setPrintOptions({ ...printOptions, includeTransportInfo: e.target.checked })
                  }
                  sx={{ color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
                />
              }
              label="Inclure les informations transport"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.includeCreatedBy}
                  onChange={(e) =>
                    setPrintOptions({ ...printOptions, includeCreatedBy: e.target.checked })
                  }
                  sx={{ color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
                />
              }
              label="Inclure le nom du créateur (Créé par)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePrintOptions} variant="outlined">
            Annuler
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' } }}
          >
            Imprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Dialog */}
      <BonLivraisonFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSuccess={handleFormSuccess}
        bonLivraisonId={editBlId}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default BonLivraisonList;
